import request from './request'

/**
 * Get the current user's daily work report.
 * @param {string} reportDate - Report date in YYYY-MM-DD format.
 * @returns {Promise<Object>} Daily work report.
 */
export function getDailyWorkReport(reportDate) {
  return request({
    url: '/work-reports/daily',
    method: 'get',
    params: { reportDate }
  })
}

/**
 * Get current user's work report access summary.
 * @returns {Promise<Object>} Work report entry visibility summary.
 */
export function getWorkReportAccessSummary() {
  return request({
    url: '/work-reports/access-summary',
    method: 'get'
  })
}

/**
 * Query the current user's historical work reports.
 * @param {Object} params - History filters and pagination fields.
 * @returns {Promise<Object>} Paginated historical reports.
 */
export function getWorkReportHistory(params) {
  return request({
    url: '/work-reports/history',
    method: 'get',
    params
  })
}

/**
 * Query current user's todos that can be selected by a work report.
 * @param {Object} params - Keyword, priority and due date filters.
 * @returns {Promise<Array>} Reportable todo snapshots.
 */
export function getReportableTodos(params) {
  return request({
    url: '/work-reports/reportable-todos',
    method: 'get',
    params
  })
}

/**
 * Get plan editing candidates for the current work report.
 * @param {number|string} reportId - Work report ID.
 * @returns {Promise<Object>} Current items, carry-over candidates and reportable todos.
 */
export function getPlanEditOptions(reportId) {
  return request({
    url: `/work-reports/${reportId}/plan/edit-options`,
    method: 'get'
  })
}

/**
 * Get todo annotations linked to one report work item.
 * @param {number|string} reportId - Work report ID.
 * @param {number|string} workItemId - Work item ID.
 * @returns {Promise<Array>} Todo annotations.
 */
export function listWorkItemTodoAnnotations(reportId, workItemId) {
  return request({
    url: `/work-reports/${reportId}/work-items/${workItemId}/todo-annotations`,
    method: 'get'
  })
}

/**
 * Add a todo annotation through one report work item.
 * @param {number|string} reportId - Work report ID.
 * @param {number|string} workItemId - Work item ID.
 * @param {Object} data - Annotation payload.
 * @returns {Promise<Object>} Created annotation.
 */
export function addWorkItemTodoAnnotation(reportId, workItemId, data) {
  return request({
    url: `/work-reports/${reportId}/work-items/${workItemId}/todo-annotations`,
    method: 'post',
    data
  })
}

/**
 * Save default report recipient preferences for the current user.
 * @param {Object} data - Recipient preference payload.
 * @returns {Promise<void>} Save result.
 */
export function saveReportRecipientPreferences(data) {
  return request({
    url: '/work-reports/recipients/preferences',
    method: 'put',
    data
  })
}

/**
 * Update recipients for one daily work report.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Recipient update payload.
 * @returns {Promise<Object>} Updated work report.
 */
export function updateDailyReportRecipients(reportId, data) {
  return request({
    url: `/work-reports/${reportId}/recipients`,
    method: 'put',
    data
  })
}

/**
 * Get the current user's personal work report reminder setting.
 * @returns {Promise<Object>} Personal reminder setting.
 */
export function getSelfWorkReportReminderSetting() {
  return request({
    url: '/work-reports/reminder-settings/self',
    method: 'get'
  })
}

/**
 * Save the current user's personal work report reminder setting.
 * @param {Object} data - Personal reminder setting payload.
 * @returns {Promise<void>} Save result.
 */
export function saveSelfWorkReportReminderSetting(data) {
  return request({
    url: '/work-reports/reminder-settings/self',
    method: 'put',
    data
  })
}

/**
 * Save a plan draft with its optimistic-lock version and request ID.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Plan draft payload.
 * @returns {Promise<Object>} Saved work report.
 */
export function savePlanDraft(reportId, data) {
  return request({
    url: `/work-reports/${reportId}/plan/draft`,
    method: 'put',
    data
  })
}

/**
 * Submit a daily plan.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Plan submission payload.
 * @returns {Promise<Object>} Submitted work report.
 */
export function submitPlan(reportId, data) {
  return request({
    url: `/work-reports/${reportId}/plan/submit`,
    method: 'post',
    data
  })
}

/**
 * Save a summary draft with its optimistic-lock version and request ID.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Summary draft payload.
 * @returns {Promise<Object>} Saved work report.
 */
export function saveSummaryDraft(reportId, data) {
  return request({
    url: `/work-reports/${reportId}/summary/draft`,
    method: 'put',
    data
  })
}

/**
 * Submit a daily summary.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Summary submission payload.
 * @returns {Promise<Object>} Submitted work report.
 */
export function submitSummary(reportId, data) {
  return request({
    url: `/work-reports/${reportId}/summary/submit`,
    method: 'post',
    data
  })
}

/**
 * Resolve carry-over items from a work report.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Carry-over handling payload.
 * @returns {Promise<Object>} Updated work report.
 */
export function resolveCarryOvers(reportId, data) {
  return request({
    url: `/work-reports/${reportId}/carryover`,
    method: 'post',
    data
  })
}

/**
 * Add a self-side comment or reply to a work report.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Comment payload.
 * @returns {Promise<void>} Comment result.
 */
export function commentWorkReport(reportId, data) {
  return request({
    url: `/work-reports/${reportId}/comments`,
    method: 'post',
    data
  })
}

/**
 * Register an attachment already uploaded to storage.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Attachment metadata.
 * @returns {Promise<Object>} Registered attachment.
 */
export function registerWorkReportAttachment(reportId, data) {
  return request({
    url: `/work-reports/${reportId}/attachments`,
    method: 'post',
    data
  })
}

/**
 * Delete a registered report attachment.
 * @param {number|string} reportId - Work report ID.
 * @param {number|string} attachmentId - Attachment ID.
 * @returns {Promise<void>} Deletion result.
 */
export function deleteWorkReportAttachment(reportId, attachmentId) {
  return request({
    url: `/work-reports/${reportId}/attachments/${attachmentId}`,
    method: 'delete'
  })
}

/**
 * Get the download URL for a registered report attachment.
 * @param {number|string} reportId - Work report ID.
 * @param {number|string} attachmentId - Attachment ID.
 * @returns {Promise<string>} Download URL.
 */
export function getWorkReportAttachmentDownloadUrl(reportId, attachmentId) {
  return request({
    url: `/work-reports/${reportId}/attachments/${attachmentId}/download-url`,
    method: 'get'
  })
}

/**
 * Get a paginated list of reports visible to a supervisor.
 * @param {Object} params - Team report filters and pagination fields.
 * @returns {Promise<Object>} Paginated team reports.
 */
export function getTeamWorkReportPage(params) {
  return request({
    url: '/work-reports/team',
    method: 'get',
    params
  })
}

/**
 * Get aggregate statistics for reports visible to a supervisor.
 * @param {Object} params - Team report scope filters.
 * @returns {Promise<Object>} Team report statistics.
 */
export function getTeamWorkReportStatistics(params) {
  return request({
    url: '/work-reports/team/statistics',
    method: 'get',
    params
  })
}

/**
 * Get a team member's work report detail.
 * @param {number|string} reportId - Work report ID.
 * @returns {Promise<Object>} Work report detail.
 */
export function getTeamWorkReportDetail(reportId) {
  return request({
    url: `/work-reports/team/${reportId}`,
    method: 'get'
  })
}

/**
 * Get a self work report phase version snapshot.
 * @param {number|string} reportId - Work report ID.
 * @param {string} phase - Report phase.
 * @param {number|string} versionNo - Phase version number.
 * @returns {Promise<Object>} Version snapshot detail.
 */
export function getWorkReportVersion(reportId, phase, versionNo) {
  return request({
    url: `/work-reports/${reportId}/versions/${phase}/${versionNo}`,
    method: 'get'
  })
}

/**
 * Get a team work report phase version snapshot.
 * @param {number|string} reportId - Work report ID.
 * @param {string} phase - Report phase.
 * @param {number|string} versionNo - Phase version number.
 * @returns {Promise<Object>} Version snapshot detail.
 */
export function getTeamWorkReportVersion(reportId, phase, versionNo) {
  return request({
    url: `/work-reports/team/${reportId}/versions/${phase}/${versionNo}`,
    method: 'get'
  })
}

/**
 * Get a paginated list of reports copied to the current user.
 * @param {Object} params - Copied report filters and pagination fields.
 * @returns {Promise<Object>} Paginated copied reports.
 */
export function getCopiedToMeWorkReportPage(params) {
  return request({
    url: '/work-reports/copied-to-me',
    method: 'get',
    params
  })
}

/**
 * Get aggregate statistics for reports copied to the current user.
 * @param {Object} params - Copied report scope filters.
 * @returns {Promise<Object>} Copied report statistics.
 */
export function getCopiedToMeWorkReportStatistics(params) {
  return request({
    url: '/work-reports/copied-to-me/statistics',
    method: 'get',
    params
  })
}

/**
 * Get a copied work report detail.
 * @param {number|string} reportId - Work report ID.
 * @returns {Promise<Object>} Work report detail.
 */
export function getCopiedToMeWorkReportDetail(reportId) {
  return request({
    url: `/work-reports/copied-to-me/${reportId}`,
    method: 'get'
  })
}

/**
 * Get a copied work report phase version snapshot.
 * @param {number|string} reportId - Work report ID.
 * @param {string} phase - Report phase.
 * @param {number|string} versionNo - Phase version number.
 * @returns {Promise<Object>} Version snapshot detail.
 */
export function getCopiedToMeWorkReportVersion(reportId, phase, versionNo) {
  return request({
    url: `/work-reports/copied-to-me/${reportId}/versions/${phase}/${versionNo}`,
    method: 'get'
  })
}

/**
 * Save a supervisor evaluation for a work report.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Evaluation payload.
 * @returns {Promise<Object>} Evaluated work report.
 */
export function evaluateWorkReport(reportId, data) {
  return request({
    url: `/work-reports/team/${reportId}/evaluations`,
    method: 'post',
    data
  })
}

/**
 * Ask a report owner to provide supplementary information.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Supplement request payload.
 * @returns {Promise<void>} Supplement request result.
 */
export function requestWorkReportSupplement(reportId, data) {
  return request({
    url: `/work-reports/team/${reportId}/supplements`,
    method: 'post',
    data
  })
}

/**
 * Add a team-side comment or reply to a work report.
 * @param {number|string} reportId - Work report ID.
 * @param {Object} data - Comment payload.
 * @returns {Promise<void>} Comment result.
 */
export function commentTeamWorkReport(reportId, data) {
  return request({
    url: `/work-reports/team/${reportId}/comments`,
    method: 'post',
    data
  })
}

/**
 * Get department work report rules manageable by the current department leader.
 * @returns {Promise<Array>} Leader department rule summaries.
 */
export function getLeaderDepartmentWorkReportRules() {
  return request({
    url: '/work-reports/rules/leader-departments',
    method: 'get'
  })
}

/**
 * Get work report rule versions for one leader-managed department.
 * @param {number|string} departmentId - Department ID.
 * @returns {Promise<Array>} Department rule versions.
 */
export function getDepartmentWorkReportRules(departmentId) {
  return request({
    url: '/work-reports/rules/department',
    method: 'get',
    params: { departmentId }
  })
}

/**
 * Save a department work report rule version.
 * @param {Object} data - Department rule payload.
 * @returns {Promise<Object>} Saved department rule.
 */
export function saveDepartmentWorkReportRule(data) {
  return request({
    url: '/work-reports/rules/department',
    method: 'post',
    data
  })
}

/**
 * Get work report rules.
 * @returns {Promise<Object>} Work report rules.
 */
export function getWorkReportRules() {
  return request({
    url: '/work-reports/admin/rules',
    method: 'get'
  })
}

/**
 * Save work report rules.
 * @param {Object} data - Work report rules payload.
 * @returns {Promise<void>} Rule save result.
 */
export function saveWorkReportRules(data) {
  return request({
    url: '/work-reports/admin/rules',
    method: 'post',
    data
  })
}

/**
 * Save administrator-managed forced recipients.
 * @param {Object} data - Forced recipient payload.
 * @returns {Promise<void>} Save result.
 */
export function saveForcedReportRecipients(data) {
  return request({
    url: '/work-reports/admin/forced-recipients',
    method: 'post',
    data
  })
}

/**
 * Save an administrator-managed exemption for an employee and date.
 * @param {Object} data - Employee/date exemption payload.
 * @returns {Promise<void>} Exemption save result.
 */
export function saveWorkReportManualExemption(data) {
  return request({
    url: '/work-reports/admin/exemptions',
    method: 'post',
    data
  })
}
